import React, { useState, useEffect, useRef } from 'react';
import { Button } from '@/components/ui';
import { Asset } from '../types/Asset';
import { ASSET_TYPE_LABELS } from '../constants/assetTypes';

/**
 * Below this many matches, every group starts open.
 *
 * Groups exist to keep 30k+ signs navigable, but they also hid small sets completely: every
 * rest area carries a parent highway number, so choosing "Rest Area" grouped all five of them
 * under two collapsed road headers and the picker looked empty. A count that small does not
 * need collapsing to stay readable.
 */
const AUTO_EXPAND_MAX_ITEMS = 25;

interface MultiTierDropdownProps {
  items: Asset[];
  value?: number;
  onChange: (value: number) => void;
  disabled?: boolean;
  loading?: boolean;
  placeholder?: string;
  noItemsMessage?: string;
  getParentDescription?: (parentAssetNumber: string) => string;
  assetType?: string; // Add assetType prop to determine display format
}

const MultiTierDropdown: React.FC<MultiTierDropdownProps> = ({
  items,
  value,
  onChange,
  disabled = false,
  loading = false,
  placeholder = 'Select an item',
  noItemsMessage = 'No items found',
  getParentDescription,
  assetType
}) => {
  const [searchQuery, setSearchQuery] = useState('');
  const [showDropdown, setShowDropdown] = useState(false);
  /** Explicit user toggles. Absent = fall back to the auto-expand rule. */
  const [groupOverrides, setGroupOverrides] = useState<Map<string, boolean>>(new Map());
  const dropdownRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setShowDropdown(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const toggleDropdown = () => {
    if (!disabled) {
      setShowDropdown(!showDropdown);
    }
  };

  const handleSearch = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchQuery(e.target.value);
  };

  const toggleGroup = (groupId: string, currentlyExpanded: boolean) => {
    setGroupOverrides(prev => {
      const next = new Map(prev);
      next.set(groupId, !currentlyExpanded);
      return next;
    });
  };

  const groupItems = (items: Asset[]) => {
    const groups: { [key: string]: Asset[] } = {};
    const ungroupedItems: Asset[] = [];

    items.forEach(item => {
      // If item has a parent asset and it's different from its own ID, group it
      if (item.parentAsset && item.parentAsset !== item.assetNumber) {
        if (!groups[item.parentAsset]) {
          groups[item.parentAsset] = [];
        }
        groups[item.parentAsset].push(item);
      } else {
        ungroupedItems.push(item);
      }
    });

    return { groups, ungroupedItems };
  };

  const getParentAssetDescription = (parentAssetNumber: string) => {
    if (getParentDescription) {
      return getParentDescription(parentAssetNumber);
    }
    
    const firstChildInGroup = items.find(item => item.parentAsset === parentAssetNumber);
    return firstChildInGroup?.description || parentAssetNumber;
  };

  const filterItems = (items: Asset[], query: string) => {
    if (!query) return items;
    
    const lowerQuery = query.toLowerCase();
    return items.filter(item => {
      // The asset's own type, raw and labelled. Without this, searching the category name
      // returns nothing: rest areas are called BEAR GLACIER and BELL 1, so "rest area"
      // matched no name, number or description and emptied the whole picker.
      const itemType = item.assetType?.toLowerCase() ?? '';
      const typeLabel = (ASSET_TYPE_LABELS as Record<string, string>)[itemType] ?? '';
      const typeMatches =
        (!!itemType && itemType.includes(lowerQuery)) ||
        (!!typeLabel && typeLabel.toLowerCase().includes(lowerQuery));

      // Direct item matching
      const itemMatches = 
        typeMatches ||
        item.name.toLowerCase().includes(lowerQuery) ||
        item.assetNumber.toLowerCase().includes(lowerQuery) ||
        (item.description || '').toLowerCase().includes(lowerQuery);

      // Parent asset matching (if getParentDescription is provided)
      if (getParentDescription && item.parentAsset) {
        const parentDescription = getParentDescription(item.parentAsset);
        const parentMatches = parentDescription.toLowerCase().includes(lowerQuery);
        return itemMatches || parentMatches;
      }

      return itemMatches;
    });
  };

  // Helper function to format asset display text
  const formatAssetDisplayText = (item: Asset) => {
    const assetTypeLower = assetType?.toLowerCase();
    const isDescriptionAsset = assetTypeLower === 'sign' || 
                              assetTypeLower === 'culvert' || 
                              assetTypeLower === 'cattleguard';
    
    if (isDescriptionAsset && item.description) {
      // For sign, culvert, and cattleguard assets, show description prominently with asset number
      return `${item.description} (${item.assetNumber})`;
    } else {
      // Default format for other asset types
      return `${item.name} (${item.assetNumber})`;
    }
  };

  const getSelectedItemText = () => {
    const selectedItem = items.find(item => item.id === value);
    return selectedItem 
      ? formatAssetDisplayText(selectedItem)
      : '';
  };

  const filteredItems = filterItems(items, searchQuery);
  const { groups, ungroupedItems } = groupItems(filteredItems);

  /**
   * Searching means the user has already narrowed things down, so show the hits rather than
   * making them open each group. Otherwise small sets start open and large ones stay collapsed,
   * with an explicit toggle always winning.
   */
  const autoExpand = filteredItems.length <= AUTO_EXPAND_MAX_ITEMS;
  const isGroupExpanded = (groupId: string) => {
    if (searchQuery) return true;
    const override = groupOverrides.get(groupId);
    return override === undefined ? autoExpand : override;
  };

  const compareLabels = (a: string, b: string) =>
    a.localeCompare(b, undefined, { sensitivity: 'base' });

  const sortedGroupEntries = Object.entries(groups).sort(([keyA], [keyB]) =>
    compareLabels(getParentAssetDescription(keyA), getParentAssetDescription(keyB))
  );

  const sortedUngroupedItems = [...ungroupedItems].sort((a, b) =>
    compareLabels(formatAssetDisplayText(a), formatAssetDisplayText(b))
  );

  return (
    <div className="multi-tier-dropdown tw-relative" ref={dropdownRef}>
      <div className="tw-flex">
        <input
          type="text"
          className="tw-block tw-w-full tw-rounded tw-border tw-border-line-input tw-bg-surface tw-px-3 tw-py-1.5 focus:tw-shadow-focus"
          value={loading ? 'Loading...' : getSelectedItemText()}
          placeholder={placeholder}
          onClick={toggleDropdown}
          readOnly
          disabled={disabled}
          style={{ cursor: 'pointer', backgroundColor: '#fff' }}
        />
        <Button
          variant="outline-secondary"
          onClick={toggleDropdown}
          disabled={disabled}
          style={{ borderTopRightRadius: '4px', borderBottomRightRadius: '4px' }}
        >
          ▼
        </Button>
      </div>

      {showDropdown && (
        <div className="tw-absolute tw-left-0 tw-min-w-full tw-w-max tw-max-w-[min(400px,calc(100vw-1rem))] tw-mt-1 tw-text-left tw-border tw-border-line tw-rounded tw-bg-surface tw-shadow-sm" style={{ zIndex: 1000 }}>
          <div className="tw-p-2">
            <input
              type="text"
              className="tw-block tw-w-full tw-rounded tw-border tw-border-line-input tw-bg-surface tw-px-3 tw-py-1.5 focus:tw-shadow-focus"
              placeholder="Search..."
              value={searchQuery}
              onChange={handleSearch}
              onClick={e => e.stopPropagation()}
              autoFocus
            />
          </div>

          <div style={{ maxHeight: '300px', overflowY: 'auto' }}>
            {sortedGroupEntries.map(([parentAsset, groupAssets]) => {
              const sortedGroupAssets = [...groupAssets].sort((a, b) =>
                compareLabels(formatAssetDisplayText(a), formatAssetDisplayText(b))
              );
              return (
              <div key={parentAsset} className="tw-border-b tw-border-line">
                <Button
                  variant="link"
                  className="tw-w-full !tw-justify-start tw-text-left tw-no-underline tw-px-3 tw-py-2"
                  title={getParentAssetDescription(parentAsset)}
                  onClick={() => toggleGroup(parentAsset, isGroupExpanded(parentAsset))}
                >
                  <span className="tw-mr-2 tw-shrink-0">
                    {isGroupExpanded(parentAsset) ? '▼' : '▶'}
                  </span>
                  <span className="tw-min-w-0 tw-truncate">{getParentAssetDescription(parentAsset)}</span>
                  {/* A collapsed group used to give no hint that anything was inside it. */}
                  <span className="tw-ml-1 tw-text-content-muted">({groupAssets.length})</span>
                </Button>
                {isGroupExpanded(parentAsset) && (
                  <div className="tw-pl-4">
                    {sortedGroupAssets.map(item => (
                      <Button
                        key={item.id}
                        variant="link"
                        title={formatAssetDisplayText(item)}
                        className={`tw-w-full !tw-justify-start tw-text-left tw-no-underline tw-px-3 tw-py-2 ${
                          value === item.id ? 'tw-bg-surface-alt' : ''
                        }`}
                        onClick={() => {
                          onChange(item.id);
                          setShowDropdown(false);
                          setSearchQuery('');
                        }}
                      >
                        <span className="tw-min-w-0 tw-truncate">{formatAssetDisplayText(item)}</span>
                      </Button>
                    ))}
                  </div>
                )}
              </div>
              );
            })}

            {sortedUngroupedItems.map(item => (
              <Button
                key={item.id}
                variant="link"
                title={formatAssetDisplayText(item)}
                className={`tw-w-full !tw-justify-start tw-text-left tw-no-underline tw-px-3 tw-py-2 ${
                  value === item.id ? 'tw-bg-surface-alt' : ''
                }`}
                onClick={() => {
                  onChange(item.id);
                  setShowDropdown(false);
                  setSearchQuery('');
                }}
              >
                <span className="tw-min-w-0 tw-truncate">{formatAssetDisplayText(item)}</span>
              </Button>
            ))}

            {!loading && filteredItems.length === 0 && (
              <div className="tw-px-3 tw-py-2 tw-text-content-muted">
                {/* Keyed off the filtered list: a search that matched nothing used to render an
                    empty panel with no explanation, because `items` was still full. */}
                {items.length === 0 ? noItemsMessage : `No assets match "${searchQuery}"`}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default MultiTierDropdown; 